import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ProgressBadge } from '../../ProgressBadge/ProgressBadge.component';
import { useStyles } from './Progress.styles';
import { Prompt, promptSize, promptType, SubText } from '../../Prompt/Prompt.component';
import {
  ExaminerFunction,
  ExaminerSampleAssessmentGroupFunction,
  VerificationState,
} from '../../../pages/Assessment/Assessment.types';
import SendbackRGZButton from '../../../pages/Assessment/SendbackRGZButton/SendbackRGZButton.component';
import { Status, AssessmentSessionProcessTypeId } from '../../../services/exams/assessmentSession/assessments.types';
import {
  sendBackAssessedSampleScore,
  sendBackAssessedSolutions,
  sendBackVerifiedSolutions,
  setAssessmentSessionEvent,
} from '../../../services/scoring/scoring.service';
import { useHistory } from 'react-router-dom';
import { getSampleAssessmentUrl, Paths } from '../../../utils/routerUrls';
import {
  getAssessmentSessionProcess,
  getProcessLabel,
  getNextProcess,
  redirectionToNextProcess,
  redirectionToProcess,
  isProcessEvaluationInProgres,
  updateSessionProcesses,
} from '../../../utils/assessmentSessionProcess';
import { AxiosResponse } from 'axios';
import {
  SendBackResult,
  SendBackAssessedExamTaskGroupSolutionsResponse,
  ProcessStatus,
  SampleAssessmentSendBackResult,
  SendBackAssessedExamTaskGroupSolutionsResult,
  SampleAssessmentSendBackResultResponse,
  ExamTaskWithoutRequiredComment,
} from '../../../services/scoring/scoring.types';
import { SolutionViewMode } from '../../../pages/Solution/Solution.component';
import { ConfirmDeny } from '../../ConfirmDeny/ConfirmDeny.component';
import useSessionUser from '../../../hooks/useSessionUser';
import { getExamTaskGroupSolutionScoreStatus } from '../../../services/scoringModel/scoringModel.service';
import { SampleExamTaskGroupSolutionScoreState } from '../../../services/scoringModel/scoringModel.types';
import Typography from '@material-ui/core/Typography';
import DoneIcon from '@mui/icons-material/Done';
import { ConfirmDenyKK } from '../../ConfirmDeny/ConfirmDenyKK/ConfirmDenyKK.component';
import { EvaluationSpinner } from '../EvaluationSpinner/EvaluationSpinner.component';
import { EvaluationResults } from '../EvaluationResults/EvaluationResults.component';
import { SignalRContext } from '../../../Context/SignalR.context';
import { CallbackMethodNames, ExaminerProcessCompleted } from '../../../models/signalR.types';
import { SampleAssessmentSessionState } from '../../../pages/Dashboard/Dashboard.types';
import { RevokeControlProcess } from '../../Revoke/RevokeControlProcess.component';
import { useScoring } from '../../../Context/Scoring.context';
import { ConversationType, Priority } from '../../../models/communication.types';
import { hasUnreadMessages } from '../../../services/communication/communication.service';
import { UnreadMessagePrompt } from '../../Prompt/UnreadMessage/UnreadMessagePrompt.component';
import { getSheetCodeWithTaskGroupName } from '../../Prompt/UnreadMessage/helpers';
import {
  sendBackAssessedSolutionsShelf,
  createExamTaskGroupSolutionScoreShelf,
} from '../../../services/exams/examTaskGroup/examTaskGroup.service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useLayoutContext } from '../../../Context/Layout.context';
import { assessmentSessionActions } from '../../../reducers/assessmentSessionReducer/assessmentSessionSlice';
import { getExamTaskWithoutRequiredCommentSubtext } from './Helpers.component';

interface ProgressProps {
  progress: number;
  examinerFunction: ExaminerFunction | undefined;
  isSpecialCaseReported: boolean;
  examTaskGroupSolutionScoreState: Status;
  examTaskGroupSolutionScoreId: string;
  mode: SolutionViewMode;
  sampleAssessmentSelectedProcess?: AssessmentSessionProcessTypeId | null;
  rgzId?: string;
  orgzId?: string;
  onSendbackOrConfirmCompleted?: () => void;
  examTaskGroupSolutionScoreVersionId?: string;
  onShelfSendBack?: () => void;
  isImageLoadError?: boolean;
  verificationState: VerificationState | null;
  isVerifierBlockedInQualification?: boolean;
  onVerifierBlockedInQualification?: () => void;
  onSendBack?: () => void;
  isExaminerBlocked?: boolean;
}
interface IPrompt {
  type: promptType;
  size?: promptSize;
  mainText: string;
  subText?: SubText[];
  subText2?: SubText[];
  enhancedText?: JSX.Element;
  mainActionButtonText: string;
  cancelButtonText: string | undefined;
  mainActionButtonCallback?: () => void;
  withoutCancelButton?: boolean;
  children?: React.ReactChild | React.ReactChild[];
}

export const Progress: React.FC<ProgressProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    assessmentSessionId,
    processTypeId: processTypeStringId,
    examinerId,
    updateSendbackState,
    isTabView,
    isSpecialCaseReported,
    isTrainingSession,
    progressPromptInFooterOpen,
    setProgressPromptInFooterOpen,
    isCommentEdit,
    examTaskGroupSolutionScoreVersionId,
    hasScoreChangedSinceLastSendback,
  } = useScoring();

  const { showLoader } = useLayoutContext();

  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const sampleAssessmentSessionStateId = assessmentSession?.sampleAssessmentSessionStateId;
  const processTypeId = parseInt(processTypeStringId) as AssessmentSessionProcessTypeId;
  const { userAssessmentSessionInfo } = useSessionUser();
  const assessmentFunction = userAssessmentSessionInfo.function;
  const sampleAssessmentGroupFunction = userAssessmentSessionInfo.sampleAssessmentGroupFunction;
  const [progressPromptInFooter, setProgressPromptInFooter] = useState<IPrompt>({
    type: 'success',
    mainText: '',
    mainActionButtonText: '',
    cancelButtonText: '',
  });
  const [enhancedText, setEnhancedText] = useState<JSX.Element>(<div></div>);
  const [sampleExamTaskGroupSolutionScoreState, setSampleExamTaskGroupSolutionScoreState] = useState<
    SampleExamTaskGroupSolutionScoreState | undefined
  >(undefined);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [unreadMessagePrompt, setUnreadMessagePrompt] = useState<{
    isOpen: boolean;
    unreadConversationType: ConversationType;
  }>({ isOpen: false, unreadConversationType: ConversationType.Standard });
  const [isfailedMissingComments, setIsfailedMissingComments] = useState<boolean>(false);
  const [isSendingBack, setIsSendingBack] = useState<boolean>(false);

  const isShelfAssessing = !!examTaskGroupSolutionScoreVersionId;
  const examTaskGroupSolutionScoreVersionIdRef = useRef(examTaskGroupSolutionScoreVersionId);
  useEffect(() => {
    examTaskGroupSolutionScoreVersionIdRef.current = examTaskGroupSolutionScoreVersionId;
  }, [examTaskGroupSolutionScoreVersionId]);

  const buildErrorPrompt = (
    failedSendbacks?: SendBackAssessedExamTaskGroupSolutionsResult[],
    sampleAssessmentSendBackResultResponse?: SampleAssessmentSendBackResultResponse,
  ) => {
    if (
      (failedSendbacks && failedSendbacks[0].sendBackResult === SendBackResult.FailedMissingComments) ||
      sampleAssessmentSendBackResultResponse?.result === SampleAssessmentSendBackResult.failedMissingComments
    ) {
      setIsfailedMissingComments(true);

      if (failedSendbacks) {
        failedSendbacks.map((result) => {
          const subtext = getExamTaskWithoutRequiredCommentSubtext(result.examTasksWithoutRequiredComment);
          setEnhancedText(subtext);
        });
      }
      if (sampleAssessmentSendBackResultResponse) {
        const subtext = getExamTaskWithoutRequiredCommentSubtext(
          sampleAssessmentSendBackResultResponse.examTasksWithoutRequiredComment,
        );
        setEnhancedText(subtext);
      }
      setProgressPromptInFooter({
        type: 'error',
        mainText: 'Nie można odesłać ocenionego RGZ, ponieważ nie został wprowadzony komentarz do:',
        mainActionButtonText: 'Powrót do RGZ',
        cancelButtonText: 'Powrót do listy',
        withoutCancelButton: isTabView,
      });
    } else {
      setProgressPromptInFooter({
        type: 'error',
        mainText: 'Wysyłanie ocenionego RGZ nie powiodło się, spróbuj ponownie później.',
        mainActionButtonText: 'Powrót do RGZ',
        cancelButtonText: 'Powrót do listy',
        withoutCancelButton: isTabView,
      });
      setEnhancedText(<div></div>);
    }
    showLoader(false);
    setProgressPromptInFooterOpen(true);
  };

  const redirectToNextProcess = async () => {
    const updatedSession = await updateSessionProcesses(assessmentSessionId, isTrainingSession);
    dispatch(assessmentSessionActions.updateAssessmentSession(updatedSession));

    redirectionToNextProcess(assessmentSessionId, examinerId, isTrainingSession, updatedSession).then((data) => {
      if (
        assessmentSession?.examinerFunction === ExaminerFunction.PZE &&
        data.nextProcess === AssessmentSessionProcessTypeId.Scoring
      ) {
        //After evaluation PZE needs to go back to the dashboard to have tokens reloaded
        history.push(Paths.dashboard);
      } else if (data && data.url) {
        if (data.nextProcess === AssessmentSessionProcessTypeId.Scoring) {
          setAssessmentSessionEvent(1).then(() => {
            sessionStorage.setItem('startWork', 'true');
            history.push(data.url);
          });
        } else {
          history.push(data.url);
        }
      }
    });
  };

  const sessionId = assessmentSession?.assessmentSessionId;

  const getBackToList = () => {
    if (props.mode === SolutionViewMode.assessment) {
      redirectionToProcess(assessmentSessionId, examinerId, processTypeId, isTrainingSession).then((data) => {
        if (data && data.url) {
          history.push(data.url);
        }
      });
    } else {
      if (sessionId) {
        history.push(getSampleAssessmentUrl(sessionId, examinerId));
      }
    }
  };

  const buildSuccessPrompt = (
    processHasFinished: boolean,
    notAdmittedToScoringProcess: boolean,
    nextProcessId?: AssessmentSessionProcessTypeId | null,
    evaluationInProgress?: boolean,
  ) => {
    if (notAdmittedToScoringProcess && processTypeId === AssessmentSessionProcessTypeId.Qualification) {
      setProgressPromptInFooter({
        type: 'error',
        mainText: `Nie zostałeś/łaś zakwalifikowany/a do procesu oceniania`,
        mainActionButtonText: 'Powrót do RGZ',
        cancelButtonText: 'Powrót do listy',
        withoutCancelButton: isTabView,
        mainActionButtonCallback: () => {
          props.onSendbackOrConfirmCompleted && props.onSendbackOrConfirmCompleted();
          redirectToNextProcess();
        },
      });
      setEnhancedText(<div></div>);
    } else if ((processHasFinished || evaluationInProgress) && nextProcessId !== null) {
      setProgressPromptInFooter({
        size: 'xs',
        type: 'info',
        mainText: `Ostatnie RGZ z pobranych w ${getProcessLabel(processTypeId).inProcess} zostało odesłane.`,
        mainActionButtonText: 'Powrót do RGZ',
        cancelButtonText: 'Powrót do listy',
        subText: evaluationInProgress
          ? [
              {
                text: <EvaluationSpinner />,
                fontSize: 16,
              },
            ]
          : [],
        mainActionButtonCallback: () => {
          props.onSendbackOrConfirmCompleted && props.onSendbackOrConfirmCompleted();
        },
      });
      setEnhancedText(<div></div>);
    } else {
      setProgressPromptInFooter({
        type: 'success',
        mainText: `RGZ: ${
          props.mode === SolutionViewMode.assessmentSampleAssessmentGroup
            ? props.rgzId
            : props.examTaskGroupSolutionScoreId
        }`,
        subText: [{ text: 'Zostało pomyślnie odesłane.' }],
        mainActionButtonText: 'Powrót do RGZ',
        cancelButtonText: 'Powrót do listy',
        withoutCancelButton: isTabView,
        mainActionButtonCallback: () => {
          props.onSendbackOrConfirmCompleted && props.onSendbackOrConfirmCompleted();
        },
      });
      setEnhancedText(<div></div>);
    }
  };

  const onPromptConfirm = () => {
    if (progressPromptInFooter.mainActionButtonCallback) {
      progressPromptInFooter.mainActionButtonCallback();
    }
    setProgressPromptInFooterOpen(false);
  };

  const refreshExamTaskGroupSolutionScoreStatus = (examTaskGroupSolutionScoreId: string) => {
    getExamTaskGroupSolutionScoreStatus(examTaskGroupSolutionScoreId)
      .then((res: AxiosResponse<SampleExamTaskGroupSolutionScoreState>) => {
        setSampleExamTaskGroupSolutionScoreState(res.data);
      })
      .catch(() => {
        showLoader(false);
        setSampleExamTaskGroupSolutionScoreState(undefined);
      });
  };

  useEffect(() => {
    if (
      props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
      ((assessmentFunction !== undefined &&
        [ExaminerFunction.KK, ExaminerFunction.KGZ, ExaminerFunction.PZWO].includes(assessmentFunction)) ||
        sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.PZWO) &&
      props.orgzId
    ) {
      refreshExamTaskGroupSolutionScoreStatus(props.orgzId);
    }
  }, [props.mode, assessmentFunction, sampleAssessmentGroupFunction, props.orgzId]);

  const updatePromptWithEvaluationResults = (
    evaluationSucceeded: boolean,
    nextProcessId?: AssessmentSessionProcessTypeId,
  ) => {
    setProgressPromptInFooter((prevState) => {
      return {
        ...prevState,
        subText: [
          {
            text: (
              <EvaluationResults
                succeeded={evaluationSucceeded}
                nextProcessId={
                  nextProcessId !== undefined
                    ? nextProcessId
                    : getNextProcess(processTypeId, getAssessmentSessionProcess())
                }
                onSuccessButtonClick={redirectToNextProcess}
              />
            ),
          },
        ],
      };
    });
    setEnhancedText(<div></div>);
  };

  SignalRContext.useSignalREffect(
    CallbackMethodNames.ExaminerCurrentProcessCompleted,
    (message: ExaminerProcessCompleted) => {
      updatePromptWithEvaluationResults(message.succeeded, message.nextProcess);
    },
    [isListening],
  );

  const handleSendBackRgzSuccess = (result: AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>) => {
    showLoader(false);
    const resultsArray = result.data.results;
    const processStatus = result.data.processStatus;
    setIsSendingBack(false);
    setIsfailedMissingComments(false);
    props.onSendBack?.();
    if (processStatus === ProcessStatus.ExaminerShouldReadMessages) {
      hasUnreadMessages(Priority.High)
        .then((result) => {
          if (result.status === 200) {
            showLoader(false);
            setUnreadMessagePrompt({
              isOpen: true,
              unreadConversationType: result.data,
            });
          }
        })
        .catch(() => {
          showLoader(false);
          buildErrorPrompt();
          setProgressPromptInFooterOpen(true);
        });
    } else {
      const processHasFinished = processStatus === ProcessStatus.Completed;
      const evaluationInProgress = isProcessEvaluationInProgres(processStatus);
      const notAdmittedToScoringProcess = processStatus === ProcessStatus.Failed;
      const nextProcessId: AssessmentSessionProcessTypeId | null = processHasFinished
        ? getNextProcess(processTypeId, getAssessmentSessionProcess())
        : null;

      if (evaluationInProgress) setIsListening(true);

      if (resultsArray[0].sendBackResult === SendBackResult.Success) {
        buildSuccessPrompt(processHasFinished, notAdmittedToScoringProcess, nextProcessId, evaluationInProgress);
        updateSendbackState(resultsArray[0].examTaskSolutionStatus);
        if (notAdmittedToScoringProcess && userAssessmentSessionInfo?.isVerifier) {
          props.onVerifierBlockedInQualification?.();
        }
        setProgressPromptInFooterOpen(true);
      } else {
        if (resultsArray[0].examTasksWithoutRequiredComment && resultsArray[0].examTasksWithoutRequiredComment.length) {
          buildErrorPrompt(resultsArray);
        } else {
          buildErrorPrompt();
        }
      }
    }
  };

  const sendBackRgzFunction = (ids: string[]) => {
    const currentVersionId = examTaskGroupSolutionScoreVersionIdRef.current;
    const currentIsShelfAssessing = !!currentVersionId;
    setIsSendingBack(true);
    showLoader(true);
    if (currentIsShelfAssessing && currentVersionId) {
      const shelfSendBack = userAssessmentSessionInfo?.isVerifier
        ? sendBackVerifiedSolutions
        : sendBackAssessedSolutionsShelf;
      shelfSendBack(ids[0], currentVersionId).then(
        (response) => {
          handleSendBackRgzSuccess(response);
          if (response.data.results && !response.data.results[0].examTasksWithoutRequiredComment.length) {
            props.onShelfSendBack && props.onShelfSendBack();
            showLoader(false);
            setIsSendingBack(false);
          }
        },
        () => {
          showLoader(false);
          setIsSendingBack(false);
          buildErrorPrompt();
          setProgressPromptInFooterOpen(true);
        },
      );
    } else if (
      props.mode === SolutionViewMode.assessment &&
      userAssessmentSessionInfo?.isVerifier &&
      [AssessmentSessionProcessTypeId.Training, AssessmentSessionProcessTypeId.Qualification].includes(processTypeId)
    ) {
      sendBackAssessedSolutions(ids).then(
        (response: AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>) => {
          showLoader(false);
          setIsSendingBack(false);
          handleSendBackRgzSuccess(response);
        },
        () => {
          showLoader(false);
          setIsSendingBack(false);
          buildErrorPrompt();
          setProgressPromptInFooterOpen(true);
        },
      );
    } else if (props.mode === SolutionViewMode.assessment && userAssessmentSessionInfo?.isVerifier) {
      createExamTaskGroupSolutionScoreShelf(ids[0]).then(
        () => {
          sendBackVerifiedSolutions(ids[0]).then(
            (response: AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>) => {
              showLoader(false);
              setIsSendingBack(false);
              handleSendBackRgzSuccess(response);
            },
            () => {
              showLoader(false);
              setIsSendingBack(false);
              buildErrorPrompt();
              setProgressPromptInFooterOpen(true);
            },
          );
        },
        () => {
          showLoader(false);
          setIsSendingBack(false);
          buildErrorPrompt();
          setProgressPromptInFooterOpen(true);
        },
      );
    } else if (props.mode === SolutionViewMode.assessment && !userAssessmentSessionInfo?.isVerifier) {
      sendBackAssessedSolutions(ids).then(
        (response: AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>) => {
          showLoader(false);
          setIsSendingBack(false);
          handleSendBackRgzSuccess(response);
        },
        () => {
          showLoader(false);
          setIsSendingBack(false);
          buildErrorPrompt();
          setProgressPromptInFooterOpen(true);
        },
      );
    } else {
      sendBackAssessedSampleScore(ids)
        .then(
          (result: AxiosResponse<SampleAssessmentSendBackResultResponse>) => {
            if (result.data.result === SampleAssessmentSendBackResult.success) {
              showLoader(false);
              setIsSendingBack(false);
              buildSuccessPrompt(true, false, null);
              updateSendbackState(Status.Assessed);
              props.onSendBack?.();
            } else {
              showLoader(false);
              setIsSendingBack(false);
              buildErrorPrompt(undefined, result.data);
            }
          },
          (result: AxiosResponse<SampleAssessmentSendBackResultResponse>) => {
            showLoader(false);
            setIsSendingBack(false);
            buildErrorPrompt(undefined, result.data);
          },
        )
        .finally(() => {
          setIsSendingBack(false);
          setProgressPromptInFooterOpen(true);
        });
    }
  };

  const isVerifierInTrainingOrQualification =
    !!userAssessmentSessionInfo?.isVerifier &&
    [AssessmentSessionProcessTypeId.Training, AssessmentSessionProcessTypeId.Qualification].includes(processTypeId);

  const isVerifierBlockedInAssesment =
    !!userAssessmentSessionInfo?.isVerifier &&
    processTypeId === AssessmentSessionProcessTypeId.Scoring &&
    props.isExaminerBlocked;

  const getProgressBarOrActionButtons = (): JSX.Element => {
    if (
      props.progress === 100 &&
      (((assessmentFunction === ExaminerFunction.CZE || isVerifierInTrainingOrQualification) &&
        props.mode === SolutionViewMode.assessment &&
        [Status.Open, Status.New].includes(props.examTaskGroupSolutionScoreState)) ||
        (sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.CZWO &&
          props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
          [Status.Open, Status.New].includes(props.examTaskGroupSolutionScoreState)) ||
        (!!userAssessmentSessionInfo?.isVerifier &&
          !isVerifierInTrainingOrQualification &&
          props.mode === SolutionViewMode.assessment &&
          [Status.Assessed, Status.Closed].includes(props.examTaskGroupSolutionScoreState)) ||
        isShelfAssessing ||
        isfailedMissingComments)
    ) {
      return (
        <>
          <div className={classes.header}>Postęp</div>
          <SendbackRGZButton
            buttonType={'single'}
            isSpecialCaseReported={props.isSpecialCaseReported || isSpecialCaseReported}
            isDisabled={
              props.isSpecialCaseReported ||
              isSpecialCaseReported ||
              isSendingBack ||
              isCommentEdit ||
              props.isImageLoadError ||
              (props.verificationState === VerificationState.scoreSelected &&
                ![ExaminerFunction.KK, ExaminerFunction.KGZ].includes(userAssessmentSessionInfo?.function)) ||
              props.isVerifierBlockedInQualification ||
              (!!userAssessmentSessionInfo?.isVerifier &&
                !isShelfAssessing &&
                !isfailedMissingComments &&
                !hasScoreChangedSinceLastSendback &&
                (props.verificationState === VerificationState.noChanges ||
                  props.verificationState === VerificationState.pendingApproval)) ||
              isVerifierBlockedInAssesment
            }
            examTaskGroupSolutionScoreIds={[props.examTaskGroupSolutionScoreId]}
            sendbackFunction={sendBackRgzFunction}
            verificationState={props.verificationState}
            isVerifierBlocked={isVerifierBlockedInAssesment}
          />
        </>
      );
    } else if (
      props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
      props.orgzId &&
      props.progress === 100 &&
      sampleExamTaskGroupSolutionScoreState !== undefined &&
      ((assessmentFunction !== undefined &&
        [ExaminerFunction.PZWO, ExaminerFunction.KGZ].includes(assessmentFunction)) ||
        sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.PZWO) &&
      sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.DuringChairmanAcceptance &&
      sessionId
    ) {
      return (
        <ConfirmDeny
          id={props.orgzId}
          sampleAssessmentSelectedProcess={props.sampleAssessmentSelectedProcess}
          progress={props.progress}
          assessmentSessionId={sessionId}
          isImageLoadError={props.isImageLoadError}
          onConfirmCompletedSuccesfully={() => {
            props.orgzId && refreshExamTaskGroupSolutionScoreStatus(props.orgzId);
            props.onSendbackOrConfirmCompleted && props.onSendbackOrConfirmCompleted();
          }}
        />
      );
    } else if (
      props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
      props.rgzId &&
      props.orgzId &&
      assessmentFunction === ExaminerFunction.KK &&
      sampleAssessmentSessionStateId === SampleAssessmentSessionState.Closed &&
      props.sampleAssessmentSelectedProcess === AssessmentSessionProcessTypeId.Control
    ) {
      return (
        <RevokeControlProcess
          examTaskGroupSolutionId={props.rgzId}
          examTaskGroupsolutionScoreId={props.orgzId}
          onRevoked={getBackToList}
          isImageLoadError={props.isImageLoadError}
        />
      );
    } else if (
      props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
      props.orgzId &&
      props.progress === 100 &&
      assessmentFunction === ExaminerFunction.KK &&
      sampleExamTaskGroupSolutionScoreState !== undefined &&
      (sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.ChairmanAccepted ||
        sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.Approved)
    ) {
      return (
        <ConfirmDenyKK
          id={props.orgzId}
          isConfirmed={sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.Approved}
          onActionCompleted={() => {
            props.orgzId && refreshExamTaskGroupSolutionScoreStatus(props.orgzId);
            props.onSendbackOrConfirmCompleted && props.onSendbackOrConfirmCompleted();
          }}
          sampleAssessmentSelectedProcess={props.sampleAssessmentSelectedProcess}
          isImageLoadError={props.isImageLoadError}
        />
      );
    } else if (
      props.mode === SolutionViewMode.assessmentSampleAssessmentGroup &&
      (assessmentFunction === ExaminerFunction.PZWO ||
        sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.PZWO) &&
      sampleExamTaskGroupSolutionScoreState === SampleExamTaskGroupSolutionScoreState.ChairmanAccepted
    ) {
      return (
        <Typography
          style={{
            display: 'inline-flex',
            color: '#347F42',
          }}
        >
          <DoneIcon />
          RGZ ZATWIERDZONE
        </Typography>
      );
    } else {
      return (
        <>
          <div className={classes.header}>Postęp</div>
          <ProgressBadge progress={props.progress} />
        </>
      );
    }
  };

  return (
    <>
      {getProgressBarOrActionButtons()}
      {progressPromptInFooterOpen && (
        <Prompt
          type={progressPromptInFooter.type}
          size={progressPromptInFooter.size}
          open={progressPromptInFooterOpen}
          isBold={true}
          mainText={progressPromptInFooter.mainText}
          subTexts={progressPromptInFooter.subText}
          enhancedText={enhancedText}
          onConfirm={onPromptConfirm}
          subTexts2={progressPromptInFooter.subText2}
          mainActionButtonText={progressPromptInFooter.mainActionButtonText}
          cancelButtonText={progressPromptInFooter.cancelButtonText}
          onCancel={progressPromptInFooter.withoutCancelButton ? undefined : getBackToList}
        />
      )}
      {unreadMessagePrompt.isOpen && assessmentSession && (
        <UnreadMessagePrompt
          action={'sendback'}
          unreadConversationType={unreadMessagePrompt.unreadConversationType}
          sheetCodeWithTaskGroupName={getSheetCodeWithTaskGroupName(
            assessmentSession.dashboardSheetCode,
            assessmentSession.examTaskGroupName,
          )}
          onConfirm={(url) => history.push(url)}
        />
      )}
    </>
  );
};
